import { Selection, Selections } from "../search/@types/Common";
import { SearchParams } from "../search/@types/SearchContainer";

interface getInteractiveSessionParamsForAnalysisParams {
  hash: string;
  analysisId: string;
}

export const commentsKeys = {
  allByHash: (sampleHash: string) => ["fetchCommentList", sampleHash]
};

export const GET_LOCAL_SAMPLE_DETAILS = "getLocalSampleDetails";

export const sampleReportKeys = {
  mergedReportKey: (hash: string, isNetworkReport = false) => [
    "rlcsMergeReport",
    hash,
    isNetworkReport
  ],
  singleReportKey: (
    hash: string,
    reportId: string,
    isNetworkReport = false
  ) => ["rlcsSingleReport", hash, reportId, isNetworkReport],
  mergedURLReportKey: (url: string) => ["rlcsMergedReportURL", url],
  analysisStatus: (hash: string) => ["rlcsAnalysisStatus", hash],
  uriStatsByUrl: (url: string) => ["uriStats", url],
  visualization: (hash: string) => ["visualization", hash],
  expandedDetailsCloud: (hash: string) => ["expandedDetailsCloud", hash],
  downloadScreenshots: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["downloadScreenshots", hash, analysisId, isNetworkReport],
  fetchExtractedScreenshots: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["fetchExtractedScreenshots", hash, analysisId, isNetworkReport],
  downloadPcaps: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["downloadPcaps", hash, analysisId, isNetworkReport],
  downloadDroppedFiles: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["downloadDroppedFiles", hash, analysisId, isNetworkReport],
  downloadMemstrings: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["downloadMemstrings", hash, analysisId, isNetworkReport],
  rlSandboxResourcesStatus: (
    hash: string,
    analysisId: string,
    isNetworkReport = false
  ) => ["rlSandboxResourcesStatus", hash, analysisId, isNetworkReport],
  historyRowStatus: (analysisId: string) => ["historyRowStatus", analysisId],
  droppedFilesToStaticAnalysis: (hash: string, analysisId: string) => [
    "droppedFilesToStaticAnalysis",
    hash,
    analysisId
  ],
  droppedFilesStaticAnalysisStatus: (analysisId: string) => [
    "droppedFilesStaticAnalysisStatus",
    analysisId
  ],
  resubmitAnalysis: (analysis: string, hash: string) => [
    "resubmit",
    analysis,
    hash
  ],
  analysisCount: (endpoint: string) => ["analysis count", endpoint],
  generalInfo: (hash?: string, analysis?: string, uploadId?: string) => [
    "general info",
    analysis,
    hash,
    uploadId
  ],
  networkCounts: (hash?: string, analysis?: string, endpoint?: string) => [
    "network count",
    hash,
    analysis,
    endpoint
  ],
  domainCounts: (hash?: string, analysis?: string, endpoint?: string) => [
    "domain count",
    hash,
    analysis,
    endpoint
  ],
  behaviorCounts: (hash: string, analysis: string, endpoint: string) => [
    "behavior count",
    hash,
    analysis,
    endpoint
  ],
  networkData: (endpoint: string) => ["network data", endpoint],
  domainData: (endpoint: string) => ["domain data", endpoint],
  behaviorData: (endpoint: string) => ["behavior data", endpoint],
  malwareData: (endpoint: string) => ["malware data", endpoint],
  mutexData: (endpoint: string) => ["mutex data", endpoint],
  processDetails: (endpoint: string) => ["process details", endpoint],
  processTree: (endpoint: string) => ["process tree", endpoint],
  joeProcessTree: (hash?: string, uploadId?: string) => [
    "joe process tree",
    hash,
    uploadId
  ],
  getBehaviorGraph: (hash?: string, analysis?: string, uploadId?: string) => [
    "get behavior graph",
    hash,
    analysis,
    uploadId
  ],
  getLocalSampleDetails: (
    hash?: string,
    sourceId?: number | string,
    relationshipId?: number | string,
    droppedSourceId?: number | string,
    parentHash?: string
  ) => [
    GET_LOCAL_SAMPLE_DETAILS,
    hash,
    sourceId,
    relationshipId,
    droppedSourceId,
    parentHash
  ],
  getYaraStringMatches: (hash: string, yaraTestRunResultId?: number) => [
    "yaraStringMatches",
    hash,
    yaraTestRunResultId
  ],
  getHexPreviewData: (
    sha1: string,
    searchAddress?: string,
    previewType?: string,
    beautify?: boolean
  ) => ["hexPreviewData", sha1, searchAddress, previewType, beautify],
  getTextPreviewData: (
    sha1: string,
    previewType: string,
    beautify?: boolean
  ) => ["textPreviewData", sha1, previewType, beautify],
  getImagePreviewThumbnail: (sha1: string) => ["imagePreviewThumbnail", sha1],
  getInteractiveSessionParamsForHash: (hash: string) => [
    "getInteractiveSessionParamsForHash",
    hash
  ],
  getInteractiveSessionParamsForAnalysis: ({
    hash,
    analysisId
  }: getInteractiveSessionParamsForAnalysisParams) => [
    "getInteractiveSessionParamsForHash",
    hash,
    analysisId
  ],
  getInteractiveSessionParamsForUploadId: (uploadId: string) => [
    "getInteractiveSessionParamsForUploadId",
    uploadId
  ],
  getLLMSummary: (
    hash?: string,
    sourceId?: number,
    relationshipId?: number,
    pregenerate?: boolean
  ) => ["llmSummary", hash, sourceId, relationshipId, pregenerate],
  getLLMSummaryHasChanged: (
    hash?: string,
    sourceId?: number,
    relationshipId?: number
  ) => ["llmSummaryHasChanged", hash, sourceId, relationshipId]
};

export const FETCH_LOCAL_DATA = "fetchLocalData";
export const FETCH_CLOUD_DATA = "fetchCloudData";
export const FETCH_USER_LIST = "fetchUserList";
export const FETCH_NETWORK_DATA = "fetchLocalNetworkData";

export const advancedSearchKeys = {
  fetchLocalDataKey: (params: SearchParams) => [
    FETCH_LOCAL_DATA,
    { ...params }
  ],
  fetchCloudDataKey: (
    params: SearchParams,
    startSearchDate: string | null | undefined,
    endSearchDate: string | null | undefined
  ) => [FETCH_CLOUD_DATA, { ...params, startSearchDate, endSearchDate }],
  fetchNetworkDataKey: (
    q?: string,
    periodType?: Selection,
    timeType?: string,
    submissionUser?: string
  ) => [FETCH_NETWORK_DATA, { q, periodType, timeType, submissionUser }],
  fetchUserData: ["fetchUserData"],
  fetchUserList: (q?: string, selections?: Selections) => [
    FETCH_USER_LIST,
    { q, selections }
  ],
  fetchListOfUrlsInAnalysisStatus: ["fetchListOfUrlsInAnalysisStatus"]
};

export const localSampleDetailsQueryKey = (
  sha1?: string,
  sampleSourceId?: number,
  relationshipId?: number
) => ["expandedDetailsLocal", sha1, sampleSourceId, relationshipId];

export const networkKeys = {
  urlReport: (url: string) => ["urlThreatIntelligenceReport", url],
  domainReport: (domain?: string) => ["domainThreatIntelligenceReport", domain],
  ipReport: (ip: string) => ["ipThreatIntelligenceReport", ip],
  whoIsReport: (url?: string, domain?: string) => [
    "whoIsReport",
    url ?? domain
  ],
  dnsReport: (url: string) => ["dnsReport", url],
  bgpReport: (ip: string) => ["bgpReport", ip],
  urlPreview: (url: string, domUrl?: string) => ["urlPreview", url, domUrl],
  urlScrapingState: (url: string) => ["urlScrapingState", url],
  usableUrlScreenshot: (urls?: Array<string>) => {
    const urlsCount = urls?.length ?? 0;
    return ["usableUrlScreenshot", urlsCount];
  },
  getJoeUrlSubmission: (url: string) => ["joe-url-submission", url]
};

export const getTiCardDataKey = (threatName: string) => [
  "ticard_data",
  threatName
];

export const getRlcsPrepareReanalyzeData = (hash: string) => [
  "rlcsPrepareReanalyzeData",
  hash
];
